'use client'

import { useEffect, useState } from 'react'
import { Loader2, Users } from 'lucide-react'
import { useSearchParams } from 'next/navigation'

import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'

import { useCardWaitlist } from '../hooks/useCardWaitlist'
import { CardReferralShare } from './CardReferralShare'

interface CardWaitlistJoinProps {
  onJoined?: () => void
}

export function CardWaitlistJoin({ onJoined }: CardWaitlistJoinProps) {
  const searchParams = useSearchParams()
  const { join, isLoading, error, isOnWaitlist, referralCode } = useCardWaitlist()
  const [referralInput, setReferralInput] = useState('')

  useEffect(() => {
    const codeFromUrl = searchParams.get('ref')?.trim()
    if (codeFromUrl) {
      setReferralInput((current) => current || codeFromUrl)
    }
  }, [searchParams])

  const handleJoin = async () => {
    try {
      await join(referralInput || undefined)
      onJoined?.()
    } catch {
      // Error state is surfaced by the useCardWaitlist hook
    }
  }

  return (
    <div className="flex justify-center">
      <Panel.Wrapper className="w-full max-w-xl">
        <Panel.Content className="flex flex-col gap-5 p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <Typography variant="h3" gradient>
              Join The Card Waitlist
            </Typography>
          </div>

          <Typography variant="p" className="text-muted-foreground">
            You are eligible for access. Join the waitlist now and share your referral link to move up faster.
          </Typography>

          <div className="flex flex-col gap-2">
            <Typography variant="prompt" className="text-muted-foreground">
              Referral code (optional)
            </Typography>
            <input
              value={referralInput}
              onChange={(event) => setReferralInput(event.target.value)}
              placeholder="Enter referral code"
              className="w-full rounded-lg border border-border bg-background/50 p-3 text-foreground focus:border-primary/50 placeholder:text-muted-foreground focus:outline-none"
              disabled={isLoading || isOnWaitlist}
            />
          </div>

          <Button onClick={handleJoin} disabled={isLoading || isOnWaitlist}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Joining...
              </span>
            ) : isOnWaitlist ? (
              'Already Joined'
            ) : (
              'Join Waitlist'
            )}
          </Button>

          {error && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
              <Typography variant="p" className="text-red-400 text-sm">
                {error.message || 'Unable to join waitlist right now.'}
              </Typography>
            </div>
          )}

          {isOnWaitlist && <CardReferralShare referralCode={referralCode} />}
        </Panel.Content>
      </Panel.Wrapper>
    </div>
  )
}
